/*
 * Copyright (c) 2026. Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useImperativeHandle, useRef, useState} from 'react';

import {ActivateHandle} from '../../../util/interfaces';

import {verifyIP} from '../../../util/requestSender';

import {ErrorType, ResponseData} from '@dvrd/dvr-controls';

import IpVerification from './ipVerification';

import {getFingerprint} from '../../../util/utils';

interface Props {
    onSuccess: (user: Record<string, any>) => void;
}

function IpVerificationController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onSuccess} = props;
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorType>(null);
    const verificationID = useRef<string | null>(null);
    const email = useRef<string | null>(null);

    function onActivate(_verificationID: string, _email: string) {
        verificationID.current = _verificationID;
        email.current = _email;
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        verificationID.current = null;
    }

    function onSubmit(code: string) {
        if (code.length !== 4) {
            setError('De code moet uit 4 cijfers bestaan');
            return;
        }
        if (!code || !verificationID.current) return;
        setLoading(true);
        getFingerprint().then((fingerprint: string) => {
            verifyIP({
                data: {
                    token: code,
                    verification_id: verificationID.current!,
                    fp: fingerprint
                },
                callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        const {user} = data;
                        onClose();
                        onSuccess(user);
                    } else setError('De code is niet correct');
                }
            });
        });
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <IpVerification
            onClose={onClose}
            onSubmit={onSubmit}
            email={email.current ?? ''}
            active={active}
            loading={loading}
            error={error}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(IpVerificationController);
